import { useCallback, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import {
  BANNER_ASPECT,
  getCroppedBannerBlob,
} from '../model/banner-image';

interface BannerImageCropperProps {
  /** Data URL ou object URL da imagem original. */
  imageSrc: string;
  onCropped: (file: File, previewUrl: string) => void;
  onCancel: () => void;
}

/** Cropper 850×1272 (aspecto do cartão) — paridade com Cropper.js do legado. */
export function BannerImageCropper({ imageSrc, onCropped, onCancel }: BannerImageCropperProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const apply = async () => {
    if (!croppedAreaPixels) return;
    setBusy(true);
    try {
      const file = await getCroppedBannerBlob(imageSrc, croppedAreaPixels);
      const previewUrl = URL.createObjectURL(file);
      onCropped(file, previewUrl);
    } catch {
      onCancel();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Ajuste o recorte na proporção do cartão (850×1272).
      </p>
      <div className="relative h-72 overflow-hidden rounded-xl bg-zinc-900 sm:h-96">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={BANNER_ASPECT}
          onCropChange={setCrop}
          onZoomChange={setZoom}
          onCropComplete={onCropComplete}
          objectFit="contain"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="banner-zoom">Zoom</Label>
        <input
          id="banner-zoom"
          type="range"
          min={1}
          max={3}
          step={0.05}
          value={zoom}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="w-full accent-primary"
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>
          Trocar imagem
        </Button>
        <Button type="button" onClick={() => void apply()} loading={busy}>
          Aplicar recorte
        </Button>
      </div>
    </div>
  );
}
